/**
 * VSL Funnel Landing — `/system` hub + `/system/:slug` premium VSL page.
 *
 * Gold-on-black cyberpunk aesthetic. Renders 3 funnels parametrically from
 * GET /api/funnel/config. Each funnel has:
 *   • Animated 6-step diagram (Framer-free CSS keyframes)
 *   • Email capture gate → unlocks hero video + subscribe counter
 *   • Double countdown urgency (top rolling 24h + bottom globally limited seats)
 *   • Trust bar + 3 bullets from the backend config
 *   • Stripe checkout CTA after unlock
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Zap, ShieldCheck, Rocket, Clock, Flame,
  ChevronRight, PlayCircle, Lock, Check, Users, TrendingUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUNNEL_STEPS = [
  { icon: Zap, labelSk: "Ad Click", labelCs: "Klik na reklamu" },
  { icon: PlayCircle, labelSk: "VSL Video", labelCs: "VSL Video" },
  { icon: Lock, labelSk: "Email Unlock", labelCs: "Email odemčení" },
  { icon: Sparkles, labelSk: "AI Analýza", labelCs: "AI Analýza" },
  { icon: TrendingUp, labelSk: "Plán +Proof", labelCs: "Plán +Důkaz" },
  { icon: Rocket, labelSk: "Launch", labelCs: "Start" },
];

// Minimal SK/CS UI strings. Defaults to SK. CS detected from navigator.language.
const UI = {
  sk: {
    eyebrow: "SYSTÉM", urgent_prefix: "Špeciálna cena platí ešte",
    hub_eyebrow: "SYSTÉM · SK/CZ · PHASE 1",
    hub_title: "Vyber si svoj systém",
    hub_sub: "Tri dokonale cielené AI systémy. Každý rieši iný problém. Vyber podľa toho, čo ťa dnes najviac tlačí.",
    hub_allsystems: "Všetky systémy",
    kind_one_time: "ONE-TIME", kind_sub: "MESAČNE",
    gate_title: "Odomkni 6-min video systém",
    gate_sub: "Napíš meno a email — video sa hneď spustí. Žiadny spam, iba tento 1 systém.",
    gate_name: "Meno", gate_email: "tvoj@email.sk",
    gate_cta: "Odomknúť video ZADARMO",
    gate_loading: "Odomykám...",
    gate_error_generic: "Nepodarilo sa. Skús to znova.",
    gate_social_proof: (n) => `🔥 ${n} ľudí odomklo video tento týždeň`,
    gate_gdpr: "🔒 Žiadny spam. GDPR-friendly. Odhláš sa 1 klikom.",
    gate_returning: "Vitaj späť — už máš prístup.",
    video_placeholder: "(Tvoj video obsah sem patrí — Loom / Wistia / YouTube embed. Zatiaľ placeholder.)",
    video_heading: "VSL video · 6 min",
    cta_countdown: (s) => `⏳ Ponuka sa odomkne za ${s}s — sleduj video`,
    bullets_title: "Čo je súčasťou",
    trust_firms: "firiem", trust_langs: "jazykov", trust_warranty: "30-day záruka",
    bottom_last: "Posledných", bottom_seats: "miest",
    bottom_wait: "Po naplnení kapacity sa ďalší launch otvorí o 2 týždne.",
    bottom_unlock: "Odomknúť video",
    loading: "Načítavam...",
    price_suffix_sub: "/mes", price_suffix_one: "",
    footer_copy: "AI SEO Platform",
  },
  cs: {
    eyebrow: "SYSTÉM", urgent_prefix: "Speciální cena platí ještě",
    hub_eyebrow: "SYSTÉM · CZ/SK · FÁZE 1",
    hub_title: "Vyber si svůj systém",
    hub_sub: "Tři precizně cílené AI systémy. Každý řeší jiný problém. Vyber podle toho, co tě dnes nejvíc tlačí.",
    hub_allsystems: "Všechny systémy",
    kind_one_time: "JEDNORÁZOVĚ", kind_sub: "MĚSÍČNĚ",
    gate_title: "Odemkni 6min video systém",
    gate_sub: "Napiš jméno a email — video se hned spustí. Žádný spam, jen tenhle 1 systém.",
    gate_name: "Jméno", gate_email: "tvůj@email.cz",
    gate_cta: "Odemknout video ZDARMA",
    gate_loading: "Odemykám...",
    gate_error_generic: "Něco se pokazilo. Zkus to znovu.",
    gate_social_proof: (n) => `🔥 ${n} lidí odemklo video tento týden`,
    gate_gdpr: "🔒 Žádný spam. GDPR-friendly. Odhlásíš se 1 kliknutím.",
    gate_returning: "Vítej zpět — už máš přístup.",
    video_placeholder: "(Tvůj video obsah sem patří — Loom / Wistia / YouTube embed. Zatím placeholder.)",
    video_heading: "VSL video · 6 min",
    cta_countdown: (s) => `⏳ Nabídka se odemkne za ${s}s — sleduj video`,
    bullets_title: "Co je součástí",
    trust_firms: "firem", trust_langs: "jazyků", trust_warranty: "30denní záruka",
    bottom_last: "Posledních", bottom_seats: "míst",
    bottom_wait: "Po naplnění kapacity se další launch otevře za 2 týdny.",
    bottom_unlock: "Odemknout video",
    loading: "Načítám...",
    price_suffix_sub: "/měs", price_suffix_one: "",
    footer_copy: "AI SEO Platforma",
  },
};

function detectLang() {
  const nav = (typeof navigator !== "undefined" ? navigator.language : "sk") || "sk";
  return nav.toLowerCase().startsWith("cs") ? "cs" : "sk";
}

// Parse the region from navigator.language (e.g. "en-US" → "US", "de-DE" → "DE").
// Falls back to a mapping from the primary subtag when no region is present.
function detectCountry() {
  if (typeof navigator === "undefined") return undefined;
  const raw = navigator.language || "";
  const parts = raw.split("-");
  if (parts.length >= 2 && parts[1].length === 2) return parts[1].toUpperCase();
  const LANG_TO_CC = {
    sk: "SK", cs: "CZ", de: "DE", pl: "PL", hu: "HU",
    fr: "FR", es: "ES", it: "IT", pt: "PT", nl: "NL",
    sv: "SE", no: "NO", da: "DK", fi: "FI", ro: "RO",
    bg: "BG", hr: "HR", uk: "UA", tr: "TR", ja: "JP",
    ko: "KR", zh: "CN", hi: "IN", ar: "SA", id: "ID",
    en: "US",
  };
  return LANG_TO_CC[(parts[0] || "").toLowerCase()];
}

function useCountdown(seconds) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    const t = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  const hh = String(Math.floor(left / 3600)).padStart(2, "0");
  const mm = String(Math.floor((left % 3600) / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return { hh, mm, ss, left };
}

// Convert a user-pasted video URL (YouTube watch, youtu.be, Vimeo, Loom,
// Wistia) into a properly embeddable iframe src with sane autoplay params.
// Returns null when input is blank or unrecognised so caller can fall back
// to the placeholder.
function toEmbedUrl(raw) {
  if (!raw) return null;
  const url = String(raw).trim();
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    // YouTube
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1` : null;
    }
    if (host.endsWith("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) return `${url}${url.includes("?") ? "&" : "?"}autoplay=1&rel=0&modestbranding=1`;
      const id = u.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1` : null;
    }
    // Vimeo
    if (host.endsWith("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean)[0];
      if (u.pathname.startsWith("/video/") || host.startsWith("player.")) return `${url}${url.includes("?") ? "&" : "?"}autoplay=1`;
      return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : null;
    }
    // Loom
    if (host.endsWith("loom.com")) {
      if (u.pathname.startsWith("/embed/")) return `${url}${url.includes("?") ? "&" : "?"}autoplay=1`;
      const parts = u.pathname.split("/").filter(Boolean);
      const id = parts[parts.length - 1];
      return id ? `https://www.loom.com/embed/${id}?autoplay=1` : null;
    }
    // Wistia / others — trust the URL as-is.
    return url;
  } catch {
    return null;
  }
}

// -------------------------------------------------------------------------
// Hub page: /system  → lists all 3 funnels as premium cards
// -------------------------------------------------------------------------

export function SystemHub() {
  const [funnels, setFunnels] = useState([]);
  const lang = detectLang();
  const t = UI[lang];
  useEffect(() => {
    axios.get(`${API}/funnel/config`).then((r) => setFunnels(r.data.funnels || [])).catch(() => {});
  }, []);
  return (
    <div style={styles.page} data-testid="system-hub">
      <Header lang={lang} />
      <section style={styles.hubHero}>
        <p style={styles.eyebrow}>{t.hub_eyebrow}</p>
        <h1 style={styles.h1}>{t.hub_title}</h1>
        <p style={styles.sub}>{t.hub_sub}</p>
      </section>
      <section style={styles.hubGrid}>
        {funnels.map((f) => (
          <Link
            key={f.slug}
            to={`/system/${f.slug}`}
            data-testid={`hub-card-${f.slug}`}
            style={{ ...styles.hubCard, borderColor: f.accent }}
          >
            <div style={{ ...styles.hubAccent, background: f.accent }} />
            <p style={styles.hubKind}>{f.type === "subscription" ? t.kind_sub : t.kind_one_time}</p>
            <h2 style={styles.hubTitle}>{f.title}</h2>
            <p style={styles.hubSub}>{f.subtitle}</p>
            <ul style={styles.hubList}>
              {f.hero_bullets?.slice(0, 3).map((b, i) => (
                <li key={i} style={styles.hubLi}>
                  <Check size={16} style={{ color: f.accent, flexShrink: 0, marginTop: 2 }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div style={styles.hubFooter}>
              <span style={{ ...styles.hubPrice, color: f.accent }}>
                €{f.price_eur}{f.type === "subscription" ? t.price_suffix_sub : t.price_suffix_one}
              </span>
              <span style={styles.hubCta}>
                {f.cta_label} <ChevronRight size={18} />
              </span>
            </div>
          </Link>
        ))}
      </section>
      <Footer />
    </div>
  );
}

// -------------------------------------------------------------------------
// Funnel detail page: /system/:slug
// -------------------------------------------------------------------------

export default function SystemFunnelPage() {
  const { slug = "klient" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [unlocked, setUnlocked] = useState(() => {
    // Returning visitor: skip the gate if they already unlocked this funnel.
    if (typeof window === "undefined") return false;
    try { return localStorage.getItem(`vsl_unlocked_${slug}`) === "1"; } catch { return false; }
  });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [error, setError] = useState(null);
  const [socialProof, setSocialProof] = useState(null);  // {last_7d}
  const [videoElapsed, setVideoElapsed] = useState(0);   // seconds since unlock
  const lang = detectLang();
  const t = UI[lang];
  const topCountdown = useCountdown(24 * 3600);
  const bottomSeats = useMemo(() => {
    const base = 17;
    return Math.max(3, base - Math.floor((24 * 3600 - topCountdown.left) / 1200));
  }, [topCountdown.left]);

  const ctaDelay = Math.max(0, Number(funnel?.cta_delay_sec ?? 0));
  const ctaUnlocked = videoElapsed >= ctaDelay;
  const ctaRemaining = Math.max(0, ctaDelay - videoElapsed);
  const embedSrc = toEmbedUrl(funnel?.video_url);

  // Creative impression tracking for A/B stats.
  useEffect(() => {
    const creativeId = params.get("utm_content");
    if (creativeId) {
      axios.post(`${API}/funnel/creative-impression?creative_id=${creativeId}`).catch(() => {});
    }
  }, [params]);

  useEffect(() => {
    axios.get(`${API}/funnel/config/${slug}`)
      .then((r) => setFunnel(r.data))
      .catch(() => navigate("/system", { replace: true }));
    // Fire-and-forget social proof fetch; failure shouldn't block the gate.
    axios.get(`${API}/funnel/${slug}/social-proof`)
      .then((r) => setSocialProof(r.data))
      .catch(() => {});
  }, [slug, navigate]);

  // Start counting elapsed "video watch" seconds as soon as the gate is
  // unlocked. Used to gate the checkout CTA behind cta_delay_sec — the
  // classic VSL pattern that materially lifts conversion.
  useEffect(() => {
    if (!unlocked) return;
    const id = setInterval(() => setVideoElapsed((v) => v + 1), 1000);
    return () => clearInterval(id);
  }, [unlocked]);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name || "Friend",
        email: form.email,
        funnel_slug: slug,
        language: lang,
        country_code: detectCountry() || (lang === "cs" ? "CZ" : "SK"),
        utm_source: params.get("utm_source") || undefined,
        utm_campaign: params.get("utm_campaign") || undefined,
        utm_medium: params.get("utm_medium") || undefined,
        utm_content: params.get("utm_content") || undefined,
      };
      await axios.post(`${API}/funnel/subscribe`, payload);
      setUnlocked(true);
      try { localStorage.setItem(`vsl_unlocked_${slug}`, "1"); } catch { /* private mode */ }
      setTimeout(() => {
        document.getElementById("vsl-video")?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    } catch (err) {
      setError(err.response?.data?.detail || t.gate_error_generic);
    } finally {
      setLoading(false);
    }
  }

  async function checkout() {
    if (!funnel) return;
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        package_id: funnel.package_id,
        origin_url: window.location.origin,
        user_email: form.email || undefined,
        language: lang,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      setError(lang === "cs"
        ? "Checkout nefunguje. Zkontroluj, zda jsi přihlášen."
        : "Checkout nefunguje. Skontroluj či si prihlásený.");
    }
  }

  if (!funnel) {
    return (
      <div style={styles.page}>
        <Header lang={lang} />
        <div style={{ textAlign: "center", padding: "120px 20px", color: "#888" }}>
          {t.loading}
        </div>
      </div>
    );
  }

  const accent = funnel.accent || "#D4AF37";

  return (
    <div style={styles.page} data-testid={`system-funnel-${slug}`}>
      {/* Top urgency bar */}
      <div style={{ ...styles.urgentBar, background: `${accent}14`, borderColor: `${accent}80` }}>
        <Flame size={14} style={{ color: accent }} />
        <span style={{ color: "#eee" }}>
          {t.urgent_prefix}&nbsp;
          <strong style={{ color: accent, fontFamily: "monospace" }}>
            {topCountdown.hh}:{topCountdown.mm}:{topCountdown.ss}
          </strong>
        </span>
      </div>

      <Header lang={lang} />

      {/* Hero */}
      <section style={styles.hero}>
        <div style={{ ...styles.heroGlow, background: `radial-gradient(circle, ${accent}33, transparent 70%)` }} />
        <p style={{ ...styles.eyebrow, color: accent }}>
          {t.eyebrow} · {funnel.persona?.toUpperCase()}
        </p>
        <h1 style={styles.h1}>{funnel.title}</h1>
        <p style={styles.heroSub}>{funnel.subtitle}</p>

        {/* 6-step animated diagram */}
        <div style={styles.diagramWrap} data-testid="funnel-diagram">
          {FUNNEL_STEPS.map((s, i) => {
            const Icon = s.icon;
            const label = lang === "cs" ? s.labelCs : s.labelSk;
            return (
              <div key={i} style={styles.diagramStep}>
                <div
                  style={{
                    ...styles.diagramDot,
                    borderColor: accent,
                    animationDelay: `${i * 0.25}s`,
                  }}
                >
                  <Icon size={20} style={{ color: accent }} />
                </div>
                <span style={styles.diagramLabel}>{label}</span>
                {i < FUNNEL_STEPS.length - 1 && (
                  <div style={{ ...styles.diagramLine, background: `linear-gradient(90deg, ${accent}, ${accent}22)` }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Video gate OR video */}
        <div id="vsl-video" style={styles.videoWrap}>
          {!unlocked ? (
            <form onSubmit={submit} style={{ ...styles.gate, borderColor: accent }} data-testid="vsl-gate-form">
              <Lock size={28} style={{ color: accent, marginBottom: 8 }} />
              <h3 style={styles.gateTitle}>{t.gate_title}</h3>
              <p style={styles.gateSub}>{t.gate_sub}</p>
              {socialProof?.last_7d > 0 && (
                <div
                  style={{ ...styles.socialProof, borderColor: `${accent}66`, color: accent }}
                  data-testid="vsl-social-proof"
                >
                  {t.gate_social_proof(socialProof.last_7d.toLocaleString(lang === "cs" ? "cs-CZ" : "sk-SK"))}
                </div>
              )}
              <input
                type="text"
                required
                placeholder={t.gate_name}
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                style={styles.input}
                data-testid="vsl-first-name"
              />
              <input
                type="email"
                required
                placeholder={t.gate_email}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                style={styles.input}
                data-testid="vsl-email"
              />
              <button
                type="submit"
                disabled={loading}
                style={{ ...styles.cta, background: accent, color: "#0a0a0a", width: "100%", justifyContent: "center" }}
                data-testid="vsl-unlock-btn"
              >
                {loading ? t.gate_loading : t.gate_cta}
              </button>
              <p style={styles.gdpr} data-testid="vsl-gdpr-line">{t.gate_gdpr}</p>
              {error && <p style={{ color: "#ff6b6b", fontSize: 13, marginTop: 10 }}>{error}</p>}
            </form>
          ) : (
            <div style={{ ...styles.videoBox, borderColor: accent }} data-testid="vsl-video-unlocked">
              {embedSrc ? (
                <div style={styles.videoFrameWrap}>
                  <iframe
                    src={embedSrc}
                    title={t.video_heading}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    style={styles.videoFrame}
                    data-testid="vsl-video-embed"
                  />
                </div>
              ) : (
                <div style={{ ...styles.videoInner, background: `linear-gradient(135deg, ${accent}22, transparent)` }}>
                  <PlayCircle size={80} style={{ color: accent }} />
                  <h3 style={{ color: "#fff", margin: "20px 0 8px" }}>{t.video_heading}</h3>
                  <p style={{ color: "#aaa", fontSize: 14, maxWidth: 500 }}>
                    {t.video_placeholder}
                  </p>
                </div>
              )}
              <div style={styles.videoCtaRow}>
                {ctaUnlocked ? (
                  <button
                    onClick={checkout}
                    style={{ ...styles.cta, background: accent, color: "#0a0a0a", animation: "ctaReveal 0.35s ease-out" }}
                    data-testid="vsl-checkout-btn"
                  >
                    {funnel.cta_label} · €{funnel.price_eur}{funnel.type === "subscription" ? t.price_suffix_sub : t.price_suffix_one}
                  </button>
                ) : (
                  <div style={{ ...styles.ctaCountdown, borderColor: `${accent}55`, color: accent }} data-testid="vsl-cta-countdown">
                    {t.cta_countdown(ctaRemaining)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bullets / value props */}
      <section style={styles.bullets}>
        <h2 style={styles.h2}>{t.bullets_title}</h2>
        <div style={styles.bulletGrid}>
          {funnel.hero_bullets?.map((b, i) => (
            <div key={i} style={{ ...styles.bulletCard, borderColor: `${accent}40` }}>
              <ShieldCheck size={22} style={{ color: accent }} />
              <p style={styles.bulletText}>{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust row */}
      <section style={styles.trust}>
        <div style={styles.trustItem}><Users size={18} style={{ color: accent }} /> 3 874+ {t.trust_firms}</div>
        <div style={styles.trustItem}><TrendingUp size={18} style={{ color: accent }} /> 26 {t.trust_langs}</div>
        <div style={styles.trustItem}><ShieldCheck size={18} style={{ color: accent }} /> {t.trust_warranty}</div>
      </section>

      {/* Bottom urgency */}
      <section style={{ ...styles.bottomBar, borderColor: accent }}>
        <Clock size={20} style={{ color: accent }} />
        <div>
          <strong style={{ color: accent }}>{t.bottom_last} {bottomSeats} {t.bottom_seats}</strong>
          <p style={{ color: "#ccc", fontSize: 13, margin: "4px 0 0" }}>
            {t.bottom_wait}
          </p>
        </div>
        <button
          onClick={() => document.getElementById("vsl-video")?.scrollIntoView({ behavior: "smooth" })}
          style={{ ...styles.cta, background: accent, color: "#0a0a0a", marginLeft: "auto" }}
          data-testid="vsl-bottom-cta"
        >
          {unlocked ? funnel.cta_label : t.bottom_unlock}
        </button>
      </section>

      <Footer lang={lang} />

      {/* Inline keyframes */}
      <style>{`
        @keyframes pulseDot { 0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 currentColor; }
                              50% { transform: scale(1.08); box-shadow: 0 0 0 6px transparent; } }
        @keyframes glow { 0%, 100% { opacity: 0.5 } 50% { opacity: 1 } }
        @keyframes ctaReveal { 0% { opacity: 0; transform: translateY(8px) scale(0.96); }
                               60% { transform: translateY(0) scale(1.04); }
                               100% { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
    </div>
  );
}

// ---- Shared chrome ------------------------------------------------------

function Header({ lang = "sk" }) {
  const t = UI[lang] || UI.sk;
  return (
    <header style={styles.header}>
      <Link to="/" style={styles.logo}>TopRankPulse</Link>
      <Link to="/system" style={styles.navLink} data-testid="nav-system-hub">{t.hub_allsystems}</Link>
    </header>
  );
}

function Footer({ lang = "sk" }) {
  const t = UI[lang] || UI.sk;
  return (
    <footer style={styles.footer}>
      <p style={{ margin: 0, color: "#555", fontSize: 12 }}>
        © 2026 TopRankPulse · {t.footer_copy} · <Link to="/privacy" style={{ color: "#888" }}>Privacy</Link>
      </p>
    </footer>
  );
}

// ---- Styles -------------------------------------------------------------

const styles = {
  page: { minHeight: "100vh", background: "#0a0a0a", color: "#fff", fontFamily: "'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 32px", borderBottom: "1px solid #1a1a1a" },
  logo: { fontWeight: 900, color: "#D4AF37", fontSize: 18, textDecoration: "none", letterSpacing: 0.5 },
  navLink: { color: "#888", textDecoration: "none", fontSize: 14, fontWeight: 600 },
  urgentBar: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 16px", fontSize: 13, borderBottom: "1px solid" },
  eyebrow: { fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "#888", marginBottom: 16 },
  h1: { fontSize: "clamp(34px, 6vw, 64px)", fontWeight: 900, margin: "0 0 20px", lineHeight: 1.05, letterSpacing: -1 },
  h2: { fontSize: 28, fontWeight: 900, textAlign: "center", margin: "0 0 32px" },
  sub: { color: "#aaa", fontSize: 18, maxWidth: 640, margin: "0 auto" },
  hero: { position: "relative", padding: "80px 24px 60px", maxWidth: 1100, margin: "0 auto", textAlign: "center", overflow: "hidden" },
  heroGlow: { position: "absolute", top: -100, left: "50%", transform: "translateX(-50%)", width: 800, height: 600, pointerEvents: "none", animation: "glow 4s ease-in-out infinite" },
  heroSub: { color: "#b0b0b0", fontSize: 18, maxWidth: 720, margin: "0 auto 40px", lineHeight: 1.55 },
  diagramWrap: { display: "flex", alignItems: "center", justifyContent: "center", gap: 0, flexWrap: "wrap", margin: "50px 0", padding: "0 12px" },
  diagramStep: { display: "flex", alignItems: "center", gap: 8, position: "relative" },
  diagramDot: { width: 48, height: 48, borderRadius: "50%", border: "2px solid", display: "flex", alignItems: "center", justifyContent: "center", background: "#0f0f0f", animation: "pulseDot 2s ease-in-out infinite" },
  diagramLabel: { fontSize: 11, color: "#aaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginRight: 4 },
  diagramLine: { width: 32, height: 2, opacity: 0.6 },
  videoWrap: { marginTop: 40, position: "relative", zIndex: 2 },
  gate: { maxWidth: 440, margin: "0 auto", padding: 32, border: "2px solid", borderRadius: 16, background: "rgba(20,20,20,0.7)", backdropFilter: "blur(10px)", textAlign: "center" },
  gateTitle: { fontSize: 22, fontWeight: 900, margin: "8px 0" },
  gateSub: { color: "#aaa", fontSize: 14, marginBottom: 20 },
  input: { width: "100%", padding: "14px 16px", background: "#0a0a0a", border: "1px solid #333", borderRadius: 10, color: "#fff", fontSize: 15, marginBottom: 12, boxSizing: "border-box" },
  cta: { padding: "14px 32px", border: "none", borderRadius: 10, fontWeight: 900, fontSize: 16, cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.5, transition: "transform 0.15s", display: "inline-flex", alignItems: "center", gap: 8 },
  videoBox: { maxWidth: 820, margin: "0 auto", border: "2px solid", borderRadius: 16, overflow: "hidden" },
  videoInner: { padding: "80px 40px", display: "flex", flexDirection: "column", alignItems: "center" },
  videoFrameWrap: { position: "relative", paddingTop: "56.25%", background: "#000" },
  videoFrame: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 },
  videoCtaRow: { padding: "20px 24px 28px", display: "flex", justifyContent: "center", background: "#0f0f0f" },
  ctaCountdown: { padding: "12px 20px", border: "1.5px dashed", borderRadius: 10, fontSize: 14, fontWeight: 800, letterSpacing: 0.3 },
  socialProof: { padding: "8px 14px", border: "1px solid", borderRadius: 999, fontSize: 12, fontWeight: 800, letterSpacing: 0.3, marginBottom: 16, display: "inline-block" },
  gdpr: { fontSize: 11, color: "#888", margin: "12px 0 0", lineHeight: 1.5 },
  bullets: { maxWidth: 1100, margin: "0 auto", padding: "80px 24px" },
  bulletGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 },
  bulletCard: { padding: 24, border: "1px solid", borderRadius: 12, background: "#111", display: "flex", gap: 14, alignItems: "flex-start" },
  bulletText: { margin: 0, color: "#ddd", fontSize: 15, lineHeight: 1.6 },
  trust: { display: "flex", justifyContent: "center", gap: 40, padding: "40px 24px", flexWrap: "wrap", borderTop: "1px solid #1a1a1a" },
  trustItem: { display: "flex", alignItems: "center", gap: 8, color: "#aaa", fontSize: 14, fontWeight: 600 },
  bottomBar: { maxWidth: 900, margin: "40px auto 60px", padding: "24px", border: "2px solid", borderRadius: 14, background: "#0f0f0f", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" },
  footer: { padding: "32px 24px", borderTop: "1px solid #1a1a1a", textAlign: "center" },
  // Hub
  hubHero: { textAlign: "center", padding: "80px 24px 40px", maxWidth: 900, margin: "0 auto" },
  hubGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 24, maxWidth: 1200, margin: "0 auto", padding: "0 24px 80px" },
  hubCard: { position: "relative", border: "2px solid", borderRadius: 16, padding: 32, background: "#0f0f0f", textDecoration: "none", color: "#fff", display: "flex", flexDirection: "column", transition: "transform 0.18s, box-shadow 0.18s" },
  hubAccent: { position: "absolute", top: 0, left: 0, right: 0, height: 4, borderRadius: "14px 14px 0 0" },
  hubKind: { fontSize: 11, fontWeight: 800, letterSpacing: 2, color: "#888", margin: "8px 0 12px" },
  hubTitle: { fontSize: 26, fontWeight: 900, margin: "0 0 10px" },
  hubSub: { color: "#aaa", fontSize: 14, lineHeight: 1.55, margin: "0 0 20px", minHeight: 60 },
  hubList: { listStyle: "none", padding: 0, margin: "0 0 24px", display: "flex", flexDirection: "column", gap: 10 },
  hubLi: { display: "flex", gap: 8, fontSize: 13, color: "#ccc", lineHeight: 1.5 },
  hubFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", paddingTop: 16, borderTop: "1px solid #1a1a1a" },
  hubPrice: { fontSize: 24, fontWeight: 900 },
  hubCta: { display: "inline-flex", alignItems: "center", gap: 4, color: "#fff", fontWeight: 800, fontSize: 14 },
};
